import {
  LATEST_FINDINGS,
  LATEST_SCORE,
  LATEST_COUNTS,
  LATEST_CHANGE_COUNT,
  CHECKS,
  CATEGORIES,
  categoryStats,
  scoreBand,
  SEVERITY_LABELS,
  computeScore,
  type CheckCategory,
  type Finding,
  type SeverityCounts,
} from "./audit-data";
import type { SfSection, AssistantContext } from "./sections";
import { buildFindingsSummary, type FindingsSummaryDto } from "./ai-api";
import { createGauge } from "../ui/gauge";
import { showToast } from "../ui/toast";

export interface SummaryDeps {
  readonly onNavigate: (section: SfSection) => void;
  readonly openAssistant: (context?: AssistantContext, summary?: FindingsSummaryDto) => void;
  // 実接続時にインジェクトされる診断結果。省略時はモックサンプルにフォールバック。
  readonly score?: number;
  readonly findings?: readonly Finding[];
  readonly counts?: SeverityCounts;
  // 接続を試みたが未接続/失効/失敗でサンプルにフォールバックした場合 true。
  // この時のみ「接続してください」誘導を表示する（apiClient 未注入の純モック表示では出さない）。
  readonly sampleMode?: boolean;
  // 接続済み実データ時に注入。「設定チェックを実施する」押下で persist 実行＋概要再描画を行う。
  // 省略時は従来のサンプル/表示中トースト挙動（実 POST は発火しない）。
  readonly onRunCheck?: () => Promise<void>;
}

export function renderSummary(root: HTMLElement, deps: SummaryDeps): void {
  root.replaceChildren();

  const activeFindings = deps.findings ?? LATEST_FINDINGS;
  const activeScore = deps.score ?? LATEST_SCORE;
  const activeCounts = deps.counts ?? LATEST_COUNTS;

  // サンプルフォールバック時の「接続してください」誘導（mock honesty）。
  if (deps.sampleMode === true) {
    const sampleNote = document.createElement("p");
    sampleNote.dataset["testid"] = "sample-notice";
    sampleNote.className = "mb-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800";
    sampleNote.textContent = "サンプルデータを表示中です。実データは「接続」タブで Salesforce と接続してください。";
    root.append(sampleNote);
  }

  const top = document.createElement("div");
  top.className = "flex flex-wrap items-center gap-8";

  const gaugeWrap = document.createElement("div");
  gaugeWrap.className = "flex flex-col items-center";
  gaugeWrap.append(createGauge({ value: activeScore, label: "Health Check", size: 200 }));
  const band = document.createElement("p");
  band.className = "mt-1 text-sm font-medium text-slate-700";
  band.textContent = "評価: " + scoreBand(activeScore) + "（Baseline Standard 比較）";
  gaugeWrap.append(band);

  const actions = document.createElement("div");
  actions.className = "space-y-3";
  const runBtn = document.createElement("button");
  runBtn.type = "button";
  runBtn.className = "block rounded bg-shinonome-700 px-4 py-2 text-sm font-medium text-white hover:bg-shinonome-800";
  runBtn.textContent = "設定チェックを実施する";
  const runStatus = document.createElement("p");
  runStatus.setAttribute("role", "status");
  runStatus.className = "text-sm text-slate-600";
  // 実データ表示時（接続済み = deps.score 注入あり）はサンプル文言を出さない（mock honesty）。
  // クリックでの再実行は SP3 スコープ。ここでは表示中の結果が実データかサンプルかを正直に伝える。
  const showingReal = deps.score !== undefined;
  const onRunCheck = deps.onRunCheck;
  runBtn.addEventListener("click", () => {
    if (onRunCheck) {
      // 接続済み実データ: persist 実行＋再描画（呼び出し側が概要を再描画する）。
      runBtn.disabled = true;
      runStatus.textContent = "設定チェックを実行中...";
      void onRunCheck().finally(() => {
        runBtn.disabled = false;
      });
      return;
    }
    // onRunCheck 無し（未接続/純モック表示）: 従来のサンプル/表示中トースト挙動（実 POST は発火しない）。
    if (showingReal) {
      runStatus.textContent = "最新の設定チェック結果を表示中です。";
      showToast({ tone: "info", title: "最新の結果を表示中", message: "Salesforce から取得した最新の診断結果です。" });
    } else {
      runStatus.textContent = "設定チェックを実施しました（サンプル・最新の結果を表示中）。";
      showToast({ tone: "info", title: "設定チェックを開始しました", message: "完了後に通知でお知らせします（サンプル）。" });
    }
  });
  const aiBtn = document.createElement("button");
  aiBtn.type = "button";
  aiBtn.className = "block rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100";
  aiBtn.textContent = "AIで分析を見る";
  aiBtn.addEventListener("click", () => {
    const summary =
      deps.findings !== undefined
        ? buildFindingsSummary(deps.findings, deps.score ?? computeScore(deps.findings))
        : undefined;
    deps.openAssistant(undefined, summary);
  });
  actions.append(runBtn, aiBtn, runStatus);

  top.append(gaugeWrap, actions);

  const sev = document.createElement("div");
  sev.className = "mt-6 flex flex-wrap gap-3";
  (["critical", "high", "medium", "low"] as const).forEach((s) => {
    const chip = document.createElement("div");
    chip.className = "rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700";
    chip.textContent = SEVERITY_LABELS[s] + " " + activeCounts[s] + " 件";
    sev.append(chip);
  });

  const change = document.createElement("button");
  change.type = "button";
  change.className = "mt-4 block text-sm text-shinonome-700 hover:underline";
  change.textContent = "前回スナップショットから " + LATEST_CHANGE_COUNT + " 件の設定変更 →";
  change.addEventListener("click", () => deps.onNavigate("snapshots"));

  // カテゴリ別カバレッジの正本: そのカテゴリに属するカタログ CHECK 数（total）。
  const catalogTotalByCategory = (category: CheckCategory): number =>
    CHECKS.filter((c) => c.category === category).length;

  const grid = document.createElement("div");
  grid.className = "mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4";
  for (const cat of CATEGORIES) {
    const stat = categoryStats(activeFindings, cat.key);
    const card = document.createElement("article");
    card.className = "flex flex-col items-center rounded-lg border border-slate-200 p-4 text-center";
    const h = document.createElement("h2");
    h.className = "text-sm font-medium text-slate-600";
    h.textContent = cat.label;
    card.append(h);

    // 実データ表示時（接続済み）はカバレッジを正直に出す（mock honesty）:
    // 未評価チェックが多いカテゴリのゲージが 100%/緑 = "完璧" に誤読されるのを防ぐ。
    // catalogTotal = カタログのチェック数, evaluated = 今回評価できた findings 数。
    const catalogTotal = catalogTotalByCategory(cat.key);
    const evaluated = stat.total;

    if (showingReal && evaluated === 0) {
      // 0件評価 → ゲージを出さず「未評価」を明示する。
      card.dataset["coverage"] = "none";
      const none = document.createElement("p");
      none.dataset["testid"] = "category-unevaluated";
      none.className =
        "mt-3 flex h-[120px] w-[120px] items-center justify-center rounded-full border border-dashed border-slate-300 text-sm font-medium text-slate-400";
      none.textContent = "未評価";
      const note = document.createElement("p");
      note.className = "mt-1 text-xs text-slate-500";
      note.textContent = "評価 0 / " + catalogTotal + " 件";
      card.append(none, note);
    } else {
      card.append(createGauge({ value: stat.score, label: cat.label, size: 120, showName: false }));
      const meta = document.createElement("p");
      meta.className = "mt-1 text-xs text-slate-500";
      if (showingReal) {
        // 部分評価のカテゴリは "評価 X/Y" を併記してゲージが全件カバーと誤読されないようにする。
        if (evaluated < catalogTotal) {
          card.dataset["coverage"] = "partial";
        }
        meta.textContent = "要対応 " + stat.fail + " 件・評価 " + evaluated + " / " + catalogTotal + " 件";
      } else {
        meta.textContent = "要対応 " + stat.fail + " / " + stat.total + " 件";
      }
      card.append(meta);
    }

    grid.append(card);
  }

  root.append(top, sev, change, grid);
}
